/**
 * Núcleo puro da Fase 2 do Equipe — adiantamento quinzenal e fechamento mensal.
 * Sem banco, sem Express: testável direto, e compartilhado com o client para as
 * PROJEÇÕES da tela ("no fechamento de 05/09: R$ 1.040,50 + variável").
 *
 * ⚠️ Projeção na tela ≠ valor gravado. Tudo que vai para o banco é calculado no
 * SERVIDOR e congelado (snapshot); o client usa estas funções só para exibir o
 * que ainda vem. Se um dia os dois divergirem, quem manda é o snapshot.
 *
 * Todo cálculo de dinheiro é feito em CENTAVOS inteiros. Somar float de reais
 * (0.1 + 0.2 === 0.30000000000000004) num valor que vai virar pagamento é como
 * a folha ganha um centavo de diferença que ninguém consegue explicar.
 */

/** Reais em string/número → centavos inteiros. Null/vazio/NaN viram 0. */
export function paraCentavos(valor: string | number | null | undefined): number {
  if (valor === null || valor === undefined || valor === "") return 0;
  const n = typeof valor === "number" ? valor : parseFloat(valor);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Centavos → string de reais com 2 casas, do jeito que o numeric(10,2) espera. */
export function paraReais(centavos: number): string {
  return (Math.round(centavos) / 100).toFixed(2);
}

/**
 * Valor do adiantamento = salário × pct%.
 *
 * Em centavos isso é literalmente `salarioEmReais × pct` (porque reais × pct/100
 * × 100 = reais × pct), o que evita uma divisão intermediária.
 * Caso real: 2081 × 50 = 104050 centavos = R$ 1.040,50.
 */
export function calcValorAdiantamento(
  salarioMensal: string | number | null | undefined,
  adiantamentoPct: string | number | null | undefined,
): string {
  const salario = typeof salarioMensal === "number" ? salarioMensal : parseFloat(String(salarioMensal ?? 0));
  const pct = typeof adiantamentoPct === "number" ? adiantamentoPct : parseFloat(String(adiantamentoPct ?? 0));
  if (!Number.isFinite(salario) || !Number.isFinite(pct)) return "0.00";
  return paraReais(Math.round(salario * pct));
}

/**
 * FÓRMULA ÚNICA do fechamento mensal, sem ramo por forma de pagamento:
 *
 *   líquido = fixo − adiantamentos pagos + variável − descontos
 *
 * Para quem recebe MENSAL o somatório de adiantamentos é 0 por construção (não
 * existe linha na competência), e o líquido cai no salário cheio. Um
 * `if (formaPagamento === 'MENSAL')` aqui seria um segundo caminho de cálculo
 * para o mesmo número — é assim que os dois divergem depois.
 */
export function calcLiquidoFechamento(args: {
  salarioFixo: string | number | null | undefined;
  adiantamentosPagos: string | number | null | undefined;
  variavelApurada: string | number | null | undefined;
  descontos: string | number | null | undefined;
}): string {
  const centavos =
    paraCentavos(args.salarioFixo)
    - paraCentavos(args.adiantamentosPagos)
    + paraCentavos(args.variavelApurada)
    - paraCentavos(args.descontos);
  return paraReais(centavos);
}

/**
 * I9 + obrigatoriedade da observação. Retorna a mensagem de erro (→ 400) ou null.
 *
 * O teto (`variavel_max_mensal`) é o combinado com a pessoa; estourar não é
 * arredondamento, é erro de digitação ou de apuração. E variável sem
 * justificativa vira um valor que ninguém consegue defender no mês seguinte —
 * por isso `variavel_obs` é obrigatória quando há variável.
 */
export function validarVariavelFechamento(args: {
  variavelApurada: string | number | null | undefined;
  variavelObs: string | null | undefined;
  variavelMaxMensal: string | number | null | undefined;
}): string | null {
  const variavel = paraCentavos(args.variavelApurada);
  if (variavel < 0) return "A variável não pode ser negativa.";

  const teto = paraCentavos(args.variavelMaxMensal);
  if (variavel > teto) {
    return `Variável de ${formatarBRL(paraReais(variavel))} acima do teto de ${formatarBRL(paraReais(teto))} desta pessoa.`;
  }
  if (variavel > 0 && !String(args.variavelObs ?? "").trim()) {
    return "Informe de onde veio a variável (observação obrigatória quando há valor).";
  }
  return null;
}

export function validarDescontosFechamento(args: {
  descontos: string | number | null | undefined;
}): string | null {
  if (paraCentavos(args.descontos) < 0) return "O desconto não pode ser negativo.";
  return null;
}

/**
 * TOTAL A SAIR da quinzena — o número do cartão "💵 Paga em" (§4.1).
 *
 * São TRÊS parcelas que nascem de fontes diferentes e nunca se misturam:
 *   • `folhaDiaristas`  — `calcFormula`, a folha de sempre (allowlist por tipo);
 *   • `adicionais`      — Fase 3 (`tarefa_adicional`); hoje entra como 0;
 *   • `adiantamentos`   — Σ dos adiantamentos do MENSALISTA nesta quinzena.
 *
 * ⚠️ Este total é DERIVADO, para exibição. Ele NÃO é gravado em
 * `quinzenas.total_bruto`, que continua sendo só a folha do diarista: aquele
 * campo é o histórico congelado das quinzenas já fechadas, e mudar o que ele
 * significa reescreveria o passado.
 *
 * O parâmetro `adicionais` já existe hoje de propósito — a Fase 3 preenche sem
 * ninguém precisar mexer nesta função nem nas colunas da tela.
 */
export function calcTotalASairQuinzena(args: {
  folhaDiaristas: string | number | null | undefined;
  adicionais?: string | number | null | undefined;
  adiantamentos?: string | number | null | undefined;
}): string {
  return paraReais(
    paraCentavos(args.folhaDiaristas)
    + paraCentavos(args.adicionais)
    + paraCentavos(args.adiantamentos),
  );
}

/**
 * Σ dos adiantamentos do mensalista nesta quinzena, em centavos.
 *
 * ⚠️ MESMA função usada pelo rodapé "Sai em" (financeiro.tsx) E pela prévia do
 * modal "Fechar Quinzena" (fechar-quinzena-dialog.tsx / FecharQuinzenaSheet.tsx),
 * sobre o MESMO array (`useMensalistasDaQuinzena`). É o que garante os dois
 * totais baterem por CONSTRUÇÃO, não por coincidência do dado do dia — achado do
 * bug do modal "Fechar Quinzena" (v2.10): duas contas calculando a mesma coisa
 * com critério diferente é a mesma classe de bug do total divergente do #119.
 */
export function totalAdiantamentosMensalistasCentavos(
  mensalistas: Array<{ adiantamento_valor: string | number | null | undefined }>,
): number {
  // ⚠️ CAIXA — soma `adiantamento_valor`, que é o adiantamento DESTA quinzena
  // (`adiantamento_id`), e nunca o da competência (`adiantamento_comp_valor`).
  // É a linha que separa "mostrar o estado certo" de "pagar duas vezes": o
  // adiantamento pago na PRIMEIRA quinzena de agosto não pode entrar no caixa
  // da segunda só porque a tela passou a enxergá-lo. Ver
  // `temAdiantamentoNaCompetencia` para a outra pergunta.
  return mensalistas.reduce((acc, m) => acc + paraCentavos(m.adiantamento_valor), 0);
}

// ─── Adiantamento automático — separação e projeção pendente ─────────────────

export interface MensalistaComValorSugerido<T> {
  mensalista: T;
  valorSugerido: string;
}

/** A linha de mensalista da quinzena, na parte que responde às DUAS perguntas. */
export interface LinhaAdiantamentoQuinzena {
  /** Adiantamento DESTA quinzena — o CAIXA. */
  adiantamento_id?: string | null;
  /** Adiantamento em QUALQUER quinzena da competência desta quinzena. */
  adiantamento_comp_id?: string | null;
  /** "Não houve adiantamento nesta competência", declarado (migração 046). */
  dispensa_comp_id?: string | null;
}

/**
 * "Esta pessoa já tem o adiantamento DESTA COMPETÊNCIA resolvido?" — adiantamento
 * em QUALQUER quinzena da competência, ou dispensa explícita.
 *
 * Nunca `adiantamento_id` sozinho (aquele é só o desta quinzena, e decide o
 * CAIXA): agosto/2026 tem duas quinzenas — a que terminou em 29/08 e a que
 * começou em 31/08 — e na segunda o adiantamento de agosto, já pago na
 * primeira, ficava invisível. A tela pedia para gerar R$ 4.401 que não faltavam,
 * e o banco recusaria (409 ADIANTAMENTO_DUPLICADO, pelo unique por competência).
 *
 * `adiantamento_id` entra como SUBCONJUNTO, não como segundo critério: o
 * adiantamento desta quinzena nasce com a competência dela
 * (`competenciaDaQuinzena`), então tê-lo já implica ter o da competência. Ele
 * é checado aqui para uma linha de origem antiga (sem as colunas novas) não
 * virar pendência falsa.
 */
export function temAdiantamentoNaCompetencia(m: LinhaAdiantamentoQuinzena): boolean {
  return m.adiantamento_comp_id != null || m.dispensa_comp_id != null || m.adiantamento_id != null;
}

/**
 * "O adiantamento SAI NESTA quinzena?" — a pergunta do CAIXA, e a única que
 * pode alimentar soma de dinheiro. Separada de `temAdiantamentoNaCompetencia`
 * de propósito: são perguntas diferentes, e foi tirar as duas do mesmo campo
 * que produziu o bug de agosto/2026.
 */
export function adiantamentoSaiNestaQuinzena(m: { adiantamento_id?: string | null }): boolean {
  return m.adiantamento_id != null;
}

/**
 * Separa os mensalistas da quinzena (bloco 2 do Financeiro) entre quem já tem
 * o adiantamento da COMPETÊNCIA resolvido e quem ainda não tem — usada pelos
 * modais de fechar quinzena (`FecharQuinzenaSheet`/`fechar-quinzena-dialog`) e
 * pela faixa `SaiJuntoNestaData`, que tinham o MESMO filtro duplicado
 * (`m.filter(m => m.adiantamento_id != null)`) e a MESMA falha: ausência de
 * registro lida como ausência de direito — mesma classe do bug que o PR #122
 * corrigiu.
 *
 * ⚠️ O critério é `temAdiantamentoNaCompetencia`, não `adiantamento_id`: numa
 * competência com duas quinzenas, quem recebeu na primeira NÃO é pendência na
 * segunda. Em compensação, `comAdiantamento` passou a conter gente cujo
 * adiantamento saiu em OUTRA quinzena — quem mostra ou soma dinheiro tem de
 * filtrar por `adiantamentoSaiNestaQuinzena` antes.
 *
 * "Sem adiantamento" só inclui quem É candidato de verdade (MENSALISTA +
 * QUINZENAL) — um mensalista `MENSAL` (1×/mês) ou alguém cujo regime mudou
 * nunca teria adiantamento automático, então não é "pendência", é normal.
 *
 * E só quem está ATIVO. `getResumoMensalistasQuinzena` traz gente inativa de
 * propósito nos ramos de fato histórico (tem diária `tipo=MENSALISTA` ou
 * adiantamento nesta quinzena) — sem este filtro, um inativo caía na lista de
 * "Gerar agora", o servidor recusava com 409 "está inativo", e como a criação é
 * em lote atômico o BATCH INTEIRO falhava: ninguém recebia adiantamento por
 * causa de uma pessoa que nem trabalha mais aqui.
 */
export function separarMensalistasPorAdiantamento<
  T extends LinhaAdiantamentoQuinzena & {
    tipo_remuneracao: string;
    forma_pagamento: string | null;
    ativo?: boolean | null;
    salario_mensal: string | number | null | undefined;
    adiantamento_pct: string | number | null | undefined;
  },
>(mensalistas: T[]): { comAdiantamento: T[]; semAdiantamento: MensalistaComValorSugerido<T>[] } {
  const comAdiantamento: T[] = [];
  const semAdiantamento: MensalistaComValorSugerido<T>[] = [];
  for (const m of mensalistas) {
    if (temAdiantamentoNaCompetencia(m)) {
      comAdiantamento.push(m);
    } else if (
      m.tipo_remuneracao === "MENSALISTA"
      && m.forma_pagamento === "QUINZENAL"
      // `ativo` ausente (linha antiga/mock sem a coluna) conta como ativo — é o
      // comportamento anterior, e só o `false` explícito exclui.
      && m.ativo !== false
    ) {
      semAdiantamento.push({ mensalista: m, valorSugerido: calcValorAdiantamento(m.salario_mensal, m.adiantamento_pct) });
    }
  }
  return { comAdiantamento, semAdiantamento };
}

export interface ProjecaoAdiantamentoPendente {
  valorQuinzena: string;
  valorFechamento: string;
}

/**
 * Rodapé honesto do card do mensalista QUINZENAL sem adiantamento gerado
 * ainda: em vez de anunciar o salário CHEIO no fechamento (o que ele só
 * receberia se a quinzena nunca fosse gerada — nunca é o cenário real),
 * projeta os DOIS pagamentos que vão existir quando o adiantamento for
 * gerado: metade (ou o `pct` combinado) agora, o resto no fechamento.
 *
 * `valorFechamento` = salário − o mesmo valor de `valorQuinzena` — não
 * "salário × (100 − pct)/100" direto, para bater centavo a centavo com o que
 * `calcLiquidoFechamento` vai realmente abater (mesma fonte, sem arredondar
 * os dois lados de formas diferentes).
 */
export function calcProjecaoAdiantamentoPendente(
  salarioMensal: string | number | null | undefined,
  adiantamentoPct: string | number | null | undefined,
): ProjecaoAdiantamentoPendente {
  const valorQuinzena = calcValorAdiantamento(salarioMensal, adiantamentoPct);
  const valorFechamento = paraReais(paraCentavos(salarioMensal) - paraCentavos(valorQuinzena));
  return { valorQuinzena, valorFechamento };
}

/** Linha de `getResumoQuinzenaPorFuncionario` tem algum valor lançado nesta quinzena? */
export function resumoQuinzenaRowTemValor(r: {
  total_bruto: string | number | null | undefined;
  total_descontos: string | number | null | undefined;
  total_adiantamentos: string | number | null | undefined;
}): boolean {
  return [r.total_bruto, r.total_descontos, r.total_adiantamentos]
    .some(v => parseFloat(String(v ?? 0)) !== 0);
}

/**
 * Modal "Fechar Quinzena" (v2.10) — separa as linhas de `getResumoQuinzenaPorFuncionario`
 * (só diarista, por desenho — §4.5) entre quem aparece na tabela de diaristas e
 * quem fica genuinamente "sem lançamento ocultado".
 *
 * Mensalista NUNCA cai em "sem lançamento": `getResumoQuinzenaPorFuncionario`
 * sempre traz ele zerado (não é isso que decide se ele "tem lançamento" — o
 * adiantamento dele mora em outra tabela, fora desta query de propósito), e sem
 * este filtro ele seria empurrado pro bucket de oculto como se não tivesse
 * nenhum valor, escondendo o adiantamento na prévia (o próprio bug reportado).
 * Quem tem diária de diarista de verdade nesta quinzena (ex.: virou mensalista
 * no meio dela) continua aparecendo normalmente — `resumoQuinzenaRowTemValor`
 * já cobre esse caso, sem precisar de tratamento especial.
 */
export function separarResumoQuinzena<
  T extends { id: string; total_bruto: any; total_descontos: any; total_adiantamentos: any },
>(
  rows: T[],
  mensalistaIds: Set<string>,
  temAdicional: (id: string) => boolean,
): { comLancamento: T[]; semLancamento: T[] } {
  const comLancamento = rows.filter(r => resumoQuinzenaRowTemValor(r) || temAdicional(r.id));
  const semLancamento = rows.filter(r =>
    !resumoQuinzenaRowTemValor(r) && !temAdicional(r.id) && !mensalistaIds.has(r.id)
  );
  return { comLancamento, semLancamento };
}

// ─── Competência ──────────────────────────────────────────────────────────────

export const COMPETENCIA_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * D11 (v2.7 — CORRIGE a regra anterior) — a competência de uma quinzena é o
 * mês em que ela COMEÇA, não o mês em que é paga.
 *
 * A quinzena 18/07–01/08 começa em julho, então é competência `2026-07` — o
 * fechamento dela sai em 05/08 (mês seguinte ao início), não em 05/09. A regra
 * antiga (`competenciaDaDataPagamento`, removida nesta correção) usava o mês
 * da `data_pagamento` — que cai no fim/depois do período — e por isso jogava
 * toda quinzena que atravessa a virada do mês uma competência inteira à
 * frente do que devia.
 *
 * Única fonte da competência de uma quinzena — todo lugar que precisa dela
 * (geração de adiantamento, cartão "Fecha mês em", card do mensalista no
 * Financeiro, tela de Fechamento mensal) chama esta função, nunca deriva o
 * mês por conta própria. Não depende de `data_pagamento` — `periodo_inicio`
 * sempre existe, então a competência nunca fica "sem dado pra calcular".
 */
export function competenciaDaQuinzena(quinzena: { periodoInicio: string }): string {
  return quinzena.periodoInicio.slice(0, 7);
}

export const MESES_COMPETENCIA = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** "agosto/2026" — o nome do mês de uma competência. Fonte única do rótulo. */
export function formatarCompetencia(competencia: string): string {
  const nome = MESES_COMPETENCIA[Number(competencia.slice(5, 7)) - 1];
  return nome ? `${nome}/${competencia.slice(0, 4)}` : competencia;
}

/** Primeiro e último dia (YYYY-MM-DD) da competência. */
export function intervaloDaCompetencia(competencia: string): { inicio: string; fim: string } {
  const ano = Number(competencia.slice(0, 4));
  const mes = Number(competencia.slice(5, 7));
  const ultimoDia = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  return {
    inicio: `${competencia}-01`,
    fim: `${competencia}-${String(ultimoDia).padStart(2, "0")}`,
  };
}

/**
 * Data em que o fechamento da competência é pago: `dia_pagamento_mensal` da org
 * aplicado ao mês. Se o dia não existe naquele mês (31 em fevereiro), cai no
 * último dia — nunca escorrega para o mês seguinte.
 */
export function dataPagamentoDaCompetencia(competencia: string, diaPagamento: number | null | undefined): string {
  const dia = Number(diaPagamento ?? 5);
  const { fim } = intervaloDaCompetencia(competencia);
  const ultimoDia = Number(fim.slice(8, 10));
  const escolhido = Math.min(Math.max(Number.isFinite(dia) ? dia : 5, 1), ultimoDia);
  return `${competencia}-${String(escolhido).padStart(2, "0")}`;
}

/** Competência seguinte — o fechamento que ainda vai acontecer. */
export function proximaCompetencia(competencia: string): string {
  const ano = Number(competencia.slice(0, 4));
  const mes = Number(competencia.slice(5, 7));
  const d = new Date(Date.UTC(ano, mes, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// ─── Regime — disjunção Diaristas × Mensalistas ────────────────────────────────

/**
 * Regime EFETIVO de um funcionário: `null`/ausente sempre vira DIARISTA (é o
 * default do cadastro — migração 024).
 */
export function ehMensalista(f: { tipoRemuneracao?: string | null }): boolean {
  return (f.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA";
}

/**
 * §3 (fix pós-#116, bug do Erivelton) — decide se uma pessoa aparece como
 * LINHA no bloco Diaristas do Financeiro. Extraída pra ser testável sem
 * renderizar React (este repo não tem runner de teste de componente — ver
 * vitest.config.ts, só inclui `server/**‍/*.test.ts`).
 *
 * O bug: a lista de linhas filtrava por REGIME ATUAL (`!ehMensalista(f)`),
 * enquanto o TOTAL somava por TIPO DA DIÁRIA (`diarias.tipo !== 'MENSALISTA'`)
 * — os dois lados do #116 (roteamento por fato histórico) só tinham sido
 * aplicados do lado do bloco Mensalistas. Quando alguém trocava de regime
 * DEPOIS de já ter diárias `NORMAL` na quinzena, a linha sumia mas o dinheiro
 * continuava no total (Erivelton: R$ 500 sumidos da lista, presentes no TOTAL).
 *
 * Regra correta, com os dois lados no mesmo critério do #116:
 *   (a) FATO HISTÓRICO — tem diária de trabalho (tipo != MENSALISTA) nesta
 *       quinzena → aparece, não importa o regime de hoje.
 *   (b) Só há um FALTOU no período, sem nenhuma diária de trabalho → aparece
 *       SÓ se o regime de HOJE não for mensalista. Essa condição continua
 *       gateada pelo regime atual de propósito: uma falta de tarefa
 *       `tipo=MENSALISTA` pertence ao bloco de Mensalistas (`dias_falta` de
 *       lá), não a este — sem o gate, volta o bug antigo do "0 vermelho" do
 *       Dáu (mensalista com falta sem nenhuma diária de trabalho, caindo aqui
 *       por engano).
 */
export function apareceComoDiarista(
  func: { tipoRemuneracao?: string | null },
  temDiariaDeTrabalho: boolean,
  temFaltouNoPeriodo: boolean,
): boolean {
  if (temDiariaDeTrabalho) return true;
  return temFaltouNoPeriodo && !ehMensalista(func);
}

/**
 * Separa uma lista de funcionários em Diaristas × Mensalistas, filtrando NA
 * ORIGEM (aqui, uma vez só) — nunca na renderização de cada bloco.
 *
 * Os dois conjuntos são disjuntos POR CONSTRUÇÃO: é o mesmo predicado
 * (`ehMensalista`) decidindo os dois lados de um if/else, nunca dois filtros
 * independentes que poderiam divergir. Usado pelo bloco Diaristas (client) e
 * pelos testes que provam a disjunção (§7.1).
 */
export function separarPorRegime<T extends { tipoRemuneracao?: string | null }>(
  funcionarios: T[],
): { diaristas: T[]; mensalistas: T[] } {
  const diaristas: T[] = [];
  const mensalistas: T[] = [];
  for (const f of funcionarios) {
    (ehMensalista(f) ? mensalistas : diaristas).push(f);
  }
  return { diaristas, mensalistas };
}

// ─── Normalização defensiva de datas vindas de SQL cru ────────────────────────

/** 'YYYY-MM-DD' — aceita string já limpa, "...T00:00:00.000Z" ou um `Date`. */
function paraDataISO(valor: unknown): string | null {
  if (valor instanceof Date) {
    // toISOString() é sempre UTC; um `date` do Postgres não tem componente de
    // hora, então o corte de 10 caracteres nunca escorrega de dia.
    return valor.toISOString().slice(0, 10);
  }
  if (typeof valor === "string") {
    // Aceita "2026-07-31" e também "2026-07-31T00:00:00.000Z" — corta na 1ª vírgula
    // ou T, nunca reformata (evitar reprocessar fuso de uma string já correta).
    const m = valor.match(/^\d{4}-\d{2}-\d{2}/);
    return m ? m[0] : null;
  }
  return null;
}

/**
 * Normaliza o retorno de um `ARRAY_AGG(coluna date)` via SQL cru num `Set` de
 * strings 'YYYY-MM-DD'.
 *
 * ⚠️ Existe porque `db.execute(sql\`...\`)` (raw, sem o mapeamento de coluna do
 * Drizzle) não garante QUE FORMATO o driver devolve para um array de `date` —
 * pode chegar como array de string, array de `Date`, ou (se o array vier como
 * texto de array do Postgres não interpretado) uma string única. Sem isto, um
 * `new Set(valorBruto).has('2026-07-31')` pode nunca bater com nada — a origem
 * real do bug é comentada em `getResumoMensalistasQuinzena` (que já força
 * `::text` na query); esta função é a segunda camada de defesa, para o dia em
 * que alguém ler o array antes do cast, ou trocar a query sem saber por quê.
 */
export function normalizarDatasISO(valor: unknown): Set<string> {
  if (!valor) return new Set();
  const bruto = Array.isArray(valor) ? valor : typeof valor === "string" ? [valor] : [];
  const limpo = bruto.map(paraDataISO).filter((d): d is string => d !== null);
  return new Set(limpo);
}

// ─── Formatação (compartilhada entre tela e mensagens de erro) ────────────────

export function formatarBRL(valor: string | number | null | undefined): string {
  const n = typeof valor === "number" ? valor : parseFloat(String(valor ?? 0));
  return (Number.isFinite(n) ? n : 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** "sáb 01/08" — o formato dos cartões de data do topo do Financeiro. */
export function formatarDataCurta(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-").map(Number);
  const dow = DIAS_SEMANA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
  return `${dow} ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
}

// ─── Fase 3 — pagamento adicional na tarefa ────────────────────────────────────

/**
 * kg × R$/kg = valor da colheita, em CENTAVOS inteiros (evita o mesmo problema
 * de arredondamento de `calcValorAdiantamento`). `valorKg` tem 4 casas
 * decimais no cadastro (R$ 0,0700), por isso o arredondamento só acontece no
 * fim, sobre o produto inteiro — nunca sobre o R$/kg isolado.
 */
export function calcValorPorKg(
  kg: string | number | null | undefined,
  valorKg: string | number | null | undefined,
): string {
  const k = typeof kg === "number" ? kg : parseFloat(String(kg ?? 0));
  const v = typeof valorKg === "number" ? valorKg : parseFloat(String(valorKg ?? 0));
  if (!Number.isFinite(k) || !Number.isFinite(v)) return "0.00";
  return paraReais(Math.round(k * v * 100));
}

/** "8.540 kg" — separador de milhar pt-BR, sem casas decimais quando inteiro. */
export function formatarKg(kg: string | number | null | undefined): string {
  const n = typeof kg === "number" ? kg : parseFloat(String(kg ?? 0));
  if (!Number.isFinite(n)) return "0 kg";
  const casas = Number.isInteger(n) ? 0 : 2;
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })} kg`;
}

/**
 * "5 colheitas · 8.540 kg × R$ 0,07" — a sublinha do agregado de adicionais no
 * Financeiro (§5.3). Singular/plural do tipo, nunca "1 colheitas".
 */
export function formatarResumoAdicional(args: {
  qtd: number;
  kgTotal: string | number | null | undefined;
  totalValor: string | number | null | undefined;
  tipoLabel: string; // "colheita" | "lançamento" etc — já no singular
}): string {
  const rotulo = `${args.qtd} ${args.qtd === 1 ? args.tipoLabel : `${args.tipoLabel}s`}`;
  const kgNum = Number(args.kgTotal ?? 0);
  if (!Number.isFinite(kgNum) || kgNum <= 0) return rotulo;

  // R$/kg MÉDIO, derivado de total ÷ kg — não um snapshot isolado. Cada
  // lançamento pode ter o R$/kg do momento em que foi feito; se o cadastro
  // mudou entre um e outro, a média é o número honesto para o agregado (o
  // detalhe por lançamento, à parte, mostra o R$/kg exato de cada um).
  const mediaCentavos = paraCentavos(args.totalValor) / kgNum;
  const media = (mediaCentavos / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 4,
  });
  return `${rotulo} · ${formatarKg(kgNum)} × ${media}`;
}

/**
 * Rótulo do MODO DE CÁLCULO do adicional — não confundir com atividade de
 * campo ("corte", "junta"). `COLHEITA`/`OUTRO` seguem sendo os valores no
 * banco e no payload da rota (migração 030); isto é só o texto de tela, único
 * lugar que decide a palavra — nunca duplicar a ternária em cada componente.
 */
export function rotuloTipoAdicional(tipo: string): string {
  return tipo === "COLHEITA" ? "Por kg" : "Valor fixo";
}

export interface AdicionalResumoPorTipo {
  tipo: "COLHEITA" | "OUTRO";
  /** ADICIONAL (+) | DESCONTO (−) — migração 046. */
  sinal: LancamentoSinal;
  qtd: number;
  /** Sempre POSITIVO, como no banco. Quem aplica o sinal é `totalAdicionaisCentavos`. */
  totalCentavos: number;
  kgTotal: number;
}

/**
 * §2.2 — agrupa o agregado de `getAdicionaisAgregadosPorQuinzena` por pessoa,
 * mantendo uma linha por TIPO (a query já vem `GROUP BY funcionario_id, tipo`
 * — nunca junta COLHEITA com OUTRO na mesma linha). Misturar os dois faria o
 * R$/kg médio de `formatarResumoAdicional` dividir pelo kg errado (um OUTRO
 * sem kg infla o total sem inflar o kg), ou por zero se a pessoa só tivesse
 * OUTRO.
 */
export function agruparAdicionaisPorFuncionario(
  agregado: Array<{ funcionario_id: string; tipo: string; sinal?: string | null; qtd: number; total: string | number; kg_total: string | number }>,
): Map<string, AdicionalResumoPorTipo[]> {
  const mapa = new Map<string, AdicionalResumoPorTipo[]>();
  for (const a of agregado) {
    const lista = mapa.get(a.funcionario_id) ?? [];
    lista.push({
      tipo: a.tipo as "COLHEITA" | "OUTRO",
      // Linha sem `sinal` (dado antigo, antes da 046) é ADICIONAL — é o que ela
      // sempre foi. Nunca inferir DESCONTO por omissão: erraria o sinal do
      // dinheiro do passado inteiro.
      sinal: a.sinal === "DESCONTO" ? "DESCONTO" : "ADICIONAL",
      qtd: a.qtd,
      totalCentavos: paraCentavos(a.total),
      kgTotal: Number(a.kg_total ?? 0),
    });
    mapa.set(a.funcionario_id, lista);
  }
  return mapa;
}

/**
 * NET das linhas (todos os tipos e os dois sinais) de uma pessoa — o total
 * combinado exibido no card e somado no "sai nesta quinzena".
 *
 * ⚠️ Aplica o sinal AQUI, num lugar só. Enquanto `tarefa_adicional` só tinha
 * adicionais, somar `valor` cru estava certo; depois da 046 um desconto somado
 * cru viraria dinheiro A MAIS — inverteria o sinal de um valor real. Todo
 * chamador desta função (FecharQuinzenaSheet, financeiro, relatórios) herda a
 * correção sem precisar saber que ela existe.
 */
export function totalAdicionaisCentavos(linhas: AdicionalResumoPorTipo[] | undefined): number {
  return (linhas ?? []).reduce(
    (acc, l) => acc + (l.sinal === "DESCONTO" ? -l.totalCentavos : l.totalCentavos),
    0,
  );
}

// ─── Fase 3 — aviso (nunca bloqueio) de estouro do teto mensal ─────────────────

export interface AvisoTetoVariavel {
  variavelCentavos: number;
  adicionalCentavos: number;
  somaCentavos: number;
  tetoCentavos: number;
  estourou: boolean;
}

/**
 * Junta variável apurada (fechamento) + adicionais do mês e compara com o teto
 * da pessoa (`funcionarios.variavel_max_mensal`). SÓ AVISO — quem já bateu a
 * meta pode trabalhar por fora e ultrapassar o teto legitimamente (decisão do
 * Hugo); o aviso serve para separar esse caso do erro de digitação, nunca para
 * bloquear o lançamento ou o fechamento.
 *
 * `null` quando a pessoa não tem teto cadastrado (`variavelMaxMensal` 0/ausente)
 * — "sem ruído no caso comum" (a maioria dos diaristas não tem teto).
 */
export function calcAvisoTetoVariavel(args: {
  variavelApurada?: string | number | null;
  adicionalCentavos: number;
  variavelMaxMensal: string | number | null | undefined;
}): AvisoTetoVariavel | null {
  const tetoCentavos = paraCentavos(args.variavelMaxMensal);
  if (tetoCentavos <= 0) return null;
  const variavelCentavos = paraCentavos(args.variavelApurada ?? 0);
  const somaCentavos = variavelCentavos + args.adicionalCentavos;
  return {
    variavelCentavos,
    adicionalCentavos: args.adicionalCentavos,
    somaCentavos,
    tetoCentavos,
    estourou: somaCentavos > tetoCentavos,
  };
}

/** "variável R$ 538,00 + adicionais R$ 300,00 = R$ 838,00 · teto R$ 538,00 — estourou" */
export function formatarAvisoTetoVariavel(a: AvisoTetoVariavel): string {
  const partes: string[] = [];
  if (a.variavelCentavos > 0) partes.push(`variável ${formatarBRL(paraReais(a.variavelCentavos))}`);
  partes.push(`adicionais ${formatarBRL(paraReais(a.adicionalCentavos))}`);
  return `${partes.join(" + ")} = ${formatarBRL(paraReais(a.somaCentavos))}`
    + ` · teto ${formatarBRL(paraReais(a.tetoCentavos))}${a.estourou ? " — estourou" : ""}`;
}

export interface AvisoTetoAdicional {
  jaLancadoCentavos: number;
  novoCentavos: number;
  somaCentavos: number;
  tetoCentavos: number;
  estourou: boolean;
}

/**
 * Variante do aviso para o BLOCO DE LANÇAMENTO (PagamentoAdicionalBlock): junta
 * o que a pessoa já tem lançado no mês com o que está em preparo agora, sem
 * envolver `variavelApurada` (que só existe no fechamento, apurado depois).
 * Mesmo espírito do aviso do fechamento, cálculo mais simples porque aqui só
 * existe uma fonte (adicionais).
 */
export function calcAvisoTetoAdicional(args: {
  jaLancadoCentavos: number;
  novoCentavos: number;
  variavelMaxMensal: string | number | null | undefined;
}): AvisoTetoAdicional | null {
  const tetoCentavos = paraCentavos(args.variavelMaxMensal);
  if (tetoCentavos <= 0) return null;
  const somaCentavos = args.jaLancadoCentavos + args.novoCentavos;
  return {
    jaLancadoCentavos: args.jaLancadoCentavos,
    novoCentavos: args.novoCentavos,
    somaCentavos,
    tetoCentavos,
    estourou: somaCentavos > tetoCentavos,
  };
}

/** "este mês R$ 838,00 · teto R$ 538,00 — estourou" */
export function formatarAvisoTetoAdicional(a: AvisoTetoAdicional): string {
  return `este mês ${formatarBRL(paraReais(a.somaCentavos))}`
    + ` · teto ${formatarBRL(paraReais(a.tetoCentavos))}${a.estourou ? " — estourou" : ""}`;
}

// ─── Lançamentos com sinal (migração 046) ─────────────────────────────────────

export type LancamentoSinal = "ADICIONAL" | "DESCONTO";

/**
 * Net de um conjunto de lançamentos, em centavos: adicionais somam, descontos
 * abatem. `valor` é SEMPRE positivo no banco — quem dá o sinal é `sinal`.
 *
 * Existe para que a conversão sinal→operação aconteça num lugar só. Espalhar
 * `sinal === 'DESCONTO' ? -v : v` por tela e query é como os dois totais da
 * mesma coisa divergem (a classe de bug do #119).
 */
export function netLancamentosCentavos(
  linhas: Array<{ sinal?: string | null; valor: string | number | null | undefined }>,
): number {
  return linhas.reduce((acc, l) => {
    const v = paraCentavos(l.valor);
    return acc + (l.sinal === "DESCONTO" ? -v : v);
  }, 0);
}

/** Rótulo de tela do sinal — nunca duplicar a ternária em cada componente. */
export function rotuloSinalLancamento(sinal: string | null | undefined): string {
  return sinal === "DESCONTO" ? "Desconto" : "Adicional";
}

/**
 * Um lançamento de DESCONTO é sempre um valor a abater — nunca um valor
 * negativo digitado. Zod já barra número negativo na entrada; isto é a segunda
 * camada, para quem chegar por outro caminho.
 */
export function validarLancamento(args: {
  sinal: string;
  valor: string | number | null | undefined;
  descricao: string | null | undefined;
}): string | null {
  if (paraCentavos(args.valor) <= 0) return "O valor do lançamento precisa ser maior que zero.";
  if (!String(args.descricao ?? "").trim()) return "Descreva o lançamento (obrigatório).";
  return null;
}

// ─── Adiantamento pago por fora e pendência da competência ────────────────────

/**
 * Validação do adiantamento EXTERNO (já pago fora do sistema).
 *
 * Observação obrigatória por desenho: este é o único caminho que grava dinheiro
 * abatendo o fechamento sem que o sistema tenha visto o pagamento acontecer. Sem
 * a frase do gestor ("pago em dinheiro em 15/08"), o abatimento vira um número
 * que ninguém consegue defender no mês seguinte — exatamente a dívida que este
 * PR está pagando.
 */
export function validarAdiantamentoExterno(args: {
  valor: string | number | null | undefined;
  pagoEm: string | null | undefined;
  observacao: string | null | undefined;
}): string | null {
  if (paraCentavos(args.valor) <= 0) return "O valor do adiantamento precisa ser maior que zero.";
  if (!args.pagoEm || !/^\d{4}-\d{2}-\d{2}$/.test(args.pagoEm)) {
    return "Informe a data real do pagamento (AAAA-MM-DD).";
  }
  if (String(args.observacao ?? "").trim().length < 5) {
    return "Explique como foi pago (ex.: \"pago em dinheiro em 15/08, fora do sistema\").";
  }
  return null;
}

export interface PendenciaAdiantamento {
  funcionarioId: string;
  nome: string;
  apelido: string | null;
  salarioMensal: string;
  adiantamentoPct: string;
  valorSugerido: string;
}

/**
 * O fechamento mensal pode ser confirmado?
 *
 * Bloqueia enquanto existir MENSALISTA + QUINZENAL ativo sem adiantamento na
 * competência E sem a dispensa explícita. As duas saídas são atos deliberados —
 * registrar o que foi pago por fora, ou declarar que não houve. Nunca há escape
 * silencioso, que é literalmente o que deixou 2026-08 passar.
 */
export function podeFecharCompetencia(pendencias: PendenciaAdiantamento[]): boolean {
  return pendencias.length === 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// Financeiro v3.1 — dois calendários, duas abas (Diaristas · quinzena |
// Mensalistas · mês). Tudo abaixo é puro e DERIVADO: nada aqui grava nada.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * D-M4 — o que de `tarefa_adicional` sai no CAIXA da quinzena.
 *
 * Desconto de MENSALISTA liquida no fechamento do mês (é semeado em
 * `fechamento_mensal.descontos` por `getBaseFechamentos`, por intervalo de
 * datas) e NÃO pode abater a quinzena também — seria dupla contagem: o mesmo
 * R$ 70 abatido no "sai nesta quinzena" e de novo no líquido do mês. Tudo o
 * mais (diarista, qualquer sinal; ADICIONAL de mensalista) continua saindo na
 * data de pagamento da quinzena, como sempre.
 *
 * Regime é o do funcionário NO MOMENTO DA LEITURA (`tipo_remuneracao` na
 * linha do agregado) — não existe histórico de regime por lançamento. Linha
 * sem `tipo_remuneracao` (dado antigo/mock) conta como diarista: é o
 * comportamento anterior, e só o MENSALISTA explícito é filtrado.
 *
 * Todo cálculo de "sai nesta quinzena" passa por aqui ANTES de
 * `netLancamentosCentavos`/`totalAdicionaisCentavos` — financeiro.tsx,
 * FecharQuinzenaSheet, fechar-quinzena-dialog. Um segundo filtro "parecido"
 * em cada tela é a classe de bug do #119.
 */
export function lancamentosNoCaixaDaQuinzena<
  T extends { sinal?: string | null; tipo_remuneracao?: string | null },
>(linhas: T[]): T[] {
  return linhas.filter(l => !(l.sinal === "DESCONTO" && l.tipo_remuneracao === "MENSALISTA"));
}

// ─── A linha do mês (resposta de GET /api/equipe/mensalistas/competencia) ────

export interface LancamentoDoMes {
  id: string;
  data: string; // 'YYYY-MM-DD'
  sinal: string; // ADICIONAL | DESCONTO
  tipo: string; // COLHEITA | OUTRO | AUXILIO
  descricao: string | null;
  /** Sempre POSITIVO — o sentido mora em `sinal`. */
  valor: string;
  quinzena_id: string;
  /** Status da quinzena que carrega este lançamento: ABERTA | FECHADA | PAGA. */
  quinzena_status?: string | null;
  /** `data_pagamento` dessa quinzena — quando o dinheiro sai (ou saiu). */
  quinzena_data_pagamento?: string | null;
}

export interface GrupoPagamentoLancamentos {
  /** PAGO = a quinzena que o carrega está PAGA. Qualquer outra coisa ainda vai sair. */
  estado: "PAGO" | "A_SAIR";
  /** Data de pagamento da quinzena; `null` quando ela ainda não tem data definida. */
  data: string | null;
  /** NET do grupo em centavos (adicional soma, desconto abate). */
  centavos: number;
  qtd: number;
}

/**
 * Agrupa lançamentos por QUANDO eles saem — a quinzena que os carrega, não a
 * data em que foram lançados.
 *
 * Existe porque a tela dizia "pago na quinzena da data · qua 05/08" imprimindo
 * a data do PRIMEIRO lançamento e chamando aquilo de pagamento. Três erros numa
 * frase só: 05/08 é quando o adicional foi lançado (a quinzena que o pagou,
 * 02–15/08, fechou em 15/08); um lançamento representava todos (os 13 do
 * Erivelton atravessam duas quinzenas, pagas em 15/08 e 29/08); e "pago" era
 * afirmado sem olhar o status da quinzena.
 *
 * "PAGO" exige `status === 'PAGA'` — FECHADA é quinzena congelada esperando o
 * dia do pagamento, e chamar isso de pago repetiria o mesmo pecado.
 */
export function agruparLancamentosPorPagamento(
  lancamentos: Array<Pick<LancamentoDoMes, "sinal" | "valor" | "quinzena_id" | "quinzena_status" | "quinzena_data_pagamento">>,
): GrupoPagamentoLancamentos[] {
  const mapa = new Map<string, GrupoPagamentoLancamentos>();
  for (const l of lancamentos) {
    const estado: "PAGO" | "A_SAIR" = l.quinzena_status === "PAGA" ? "PAGO" : "A_SAIR";
    const data = l.quinzena_data_pagamento ?? null;
    const chave = `${estado}|${data ?? ""}`;
    const grupo = mapa.get(chave) ?? { estado, data, centavos: 0, qtd: 0 };
    grupo.centavos += netLancamentosCentavos([l]);
    grupo.qtd += 1;
    mapa.set(chave, grupo);
  }
  // PAGO antes de A_SAIR; dentro de cada um, por data (sem data por último).
  return Array.from(mapa.values()).sort((a, b) => {
    if (a.estado !== b.estado) return a.estado === "PAGO" ? -1 : 1;
    if (a.data === b.data) return 0;
    if (!a.data) return 1;
    if (!b.data) return -1;
    return a.data.localeCompare(b.data);
  });
}

/**
 * A sublinha honesta da coluna Adicionais: "pago em sáb 15/08 e sáb 29/08",
 * "sai em sáb 12/09", ou os dois lados quando o mês atravessa quinzenas em
 * estados diferentes. `null` quando não há o que dizer.
 */
export function textoPagamentoLancamentos(grupos: GrupoPagamentoLancamentos[]): string | null {
  if (grupos.length === 0) return null;
  const frase = (estado: "PAGO" | "A_SAIR") => {
    const datas = grupos.filter(g => g.estado === estado).map(g => g.data);
    if (datas.length === 0) return null;
    const verbo = estado === "PAGO" ? "pago" : "sai";
    // Sem data definida não dá para prometer dia nenhum.
    if (datas.every(d => !d)) return estado === "PAGO" ? "pago na quinzena" : "sai na quinzena";
    const rotulos = datas.filter((d): d is string => !!d).map(formatarDataCurta);
    const lista = rotulos.length === 1
      ? rotulos[0]
      : `${rotulos.slice(0, -1).join(", ")} e ${rotulos[rotulos.length - 1]}`;
    return `${verbo} em ${lista}`;
  };
  return [frase("PAGO"), frase("A_SAIR")].filter(Boolean).join(" · ") || null;
}

export interface AdiantamentoDoMes {
  id: string;
  valor: string;
  status: string; // PREVISTO | PAGO
  origem: string; // AUTOMATICO | MANUAL | EXTERNO
  pago_em: string | null; // 'YYYY-MM-DD'
  observacao: string | null;
  quinzena_id: string;
  quinzena_periodo_inicio: string | null;
  quinzena_periodo_fim: string | null;
  quinzena_data_pagamento: string | null;
  quinzena_status: string | null;
}

export interface FechamentoDaLinhaMes {
  id: string;
  status: string; // ABERTO | FECHADO
  salario_fixo: string | null;
  adiantamentos_pagos: string | null;
  variavel_apurada: string | null;
  variavel_obs: string | null;
  descontos: string | null;
  descontos_obs: string | null;
  liquido_final: string | null;
  dias_registro: number | null;
  dias_falta: number | null;
  fechado_em: string | null; // 'YYYY-MM-DD'
  /** Quando o fechamento foi GERADO — o "gerado em dd/mm" da faixa de estado. */
  criado_em: string | null;
}

export interface LinhaMensalistaMes {
  id: string;
  nome: string;
  apelido: string | null;
  ativo: boolean;
  tipo_remuneracao: string;
  /** CADASTRO DE HOJE — só para a projeção. Com fechamento, vale o snapshot. */
  salario_mensal: string | null;
  forma_pagamento: string | null;
  adiantamento_pct: string | null;
  variavel_max_mensal: string | null;
  salario_modo: string | null;
  encarregado: boolean;
  setores_encarregado: string[];
  dias_registro: number;
  datas_registro: string[];
  dias_falta: number;
  datas_falta: string[];
  adiantamento: AdiantamentoDoMes | null;
  dispensa: { id: string; motivo: string } | null;
  /** Σ ADICIONAL do mês civil (inclui auxílio) — informativo; sai na quinzena. */
  adicionais_centavos: number;
  /** Σ ADICIONAL tipo=AUXILIO — não conta no teto do variável. */
  auxilios_centavos: number;
  /** Σ DESCONTO do mês civil — abate SÓ no fechamento (D-M4). */
  descontos_centavos: number;
  lancamentos: LancamentoDoMes[];
  fechamento: FechamentoDaLinhaMes | null;
  /** Tem fato no mês (registro/adiantamento/dispensa/fechamento) mas hoje não é MENSALISTA. */
  era_mensalista: boolean;
}

export type EstadoLinhaMes = "projecao" | "aberto" | "fechado";
/** `vazio` = nenhuma linha (mês sem mensalista). */
export type EstadoDoMes = "projecao" | "gerado" | "pago" | "vazio";

/** D-M6 — sem linha em `fechamento_mensal` = projeção; ABERTO = gerado (a pagar); FECHADO = pago. */
export function estadoLinhaMes(l: Pick<LinhaMensalistaMes, "fechamento">): EstadoLinhaMes {
  if (!l.fechamento) return "projecao";
  return l.fechamento.status === "FECHADO" ? "fechado" : "aberto";
}

/**
 * Estado do MÊS, derivado das linhas: nenhum fechamento = projeção; algum
 * ABERTO = gerado; todos FECHADO = pago. Linha `era_mensalista` sem fechamento
 * não segura o mês em "projeção" — ela não vai ter fechamento nenhum.
 */
export function estadoDoMes(linhas: Array<Pick<LinhaMensalistaMes, "fechamento" | "era_mensalista">>): EstadoDoMes {
  const relevantes = linhas.filter(l => l.fechamento || !l.era_mensalista);
  if (relevantes.length === 0) return "vazio";
  const estados = relevantes.map(estadoLinhaMes);
  if (estados.some(e => e === "aberto")) return "gerado";
  if (estados.every(e => e === "fechado")) return "pago";
  return "projecao";
}

export type SituacaoAdiantamentoMes =
  | "pago_quinzena"   // PAGO, veio da cascata do fechamento da quinzena
  | "pago_fora"       // PAGO, origem EXTERNO
  | "previsto"        // PREVISTO — vira PAGO quando a quinzena fechar
  | "dispensado"      // "não houve adiantamento" registrado
  | "a_gerar"         // QUINZENAL ativo sem adiantamento e sem dispensa
  | "mensal"          // recebe 1×/mês — não tem adiantamento por desenho
  | "nao_se_aplica";  // era_mensalista / inativo sem nada

/** Como o adiantamento da linha se apresenta na aba Mês (sublinha da coluna). */
export function situacaoAdiantamentoMes(
  l: Pick<LinhaMensalistaMes, "adiantamento" | "dispensa" | "forma_pagamento" | "tipo_remuneracao" | "ativo" | "era_mensalista">,
): SituacaoAdiantamentoMes {
  const a = l.adiantamento;
  if (a) {
    if (a.status === "PAGO") return a.origem === "EXTERNO" ? "pago_fora" : "pago_quinzena";
    return "previsto";
  }
  if (l.dispensa) return "dispensado";
  if ((l.forma_pagamento ?? "MENSAL") !== "QUINZENAL") return "mensal";
  if (l.tipo_remuneracao === "MENSALISTA" && l.ativo !== false) return "a_gerar";
  return "nao_se_aplica";
}

/** Salário que a tela mostra: SNAPSHOT quando há fechamento, cadastro só na projeção. */
export function salarioLinhaMes(l: Pick<LinhaMensalistaMes, "salario_mensal" | "fechamento">): string {
  return l.fechamento ? paraReais(paraCentavos(l.fechamento.salario_fixo)) : paraReais(paraCentavos(l.salario_mensal));
}

/**
 * Argumentos da PROJEÇÃO do líquido — o único lugar que decide o que entra em
 * `calcLiquidoFechamento` quando ainda não existe fechamento.
 *
 * São os MESMOS argumentos que `getBaseFechamentos` vai congelar na geração:
 * salário do cadastro, adiantamento da competência, variável 0 (é digitada no
 * fechamento), descontos = Σ lançamentos de DESCONTO do mês civil. A única
 * antecipação é o adiantamento: `getBaseFechamentos` só soma `status='PAGO'`,
 * mas como o fechamento só pode ser gerado depois que a quinzena fechou (e
 * fechar a quinzena é o que vira PREVISTO em PAGO — D13), o PREVISTO de hoje
 * É o PAGO de amanhã, e projetar salário cheio nesse intervalo seria
 * exatamente o "TOTAL R$ 0,00 / R$ 1.075" que confundiu o print de 07/09.
 * Quem ainda está `a gerar` projeta com o valor que a geração usaria
 * (`calcValorAdiantamento`) — e a linha fica vermelha até alguém gerar,
 * registrar por fora ou dispensar; o servidor trava o fechamento de qualquer
 * jeito (409 ADIANTAMENTO_PENDENTE).
 *
 * Exibição, nunca gravação: o snapshot é do servidor. Se divergirem, manda o snapshot.
 */
export function argsProjecaoLinhaMes(
  l: Pick<LinhaMensalistaMes, "salario_mensal" | "adiantamento" | "dispensa" | "forma_pagamento" | "tipo_remuneracao" | "ativo" | "era_mensalista" | "adiantamento_pct" | "descontos_centavos">,
): { salarioFixo: string; adiantamentosPagos: string; variavelApurada: number; descontos: string } {
  const situacao = situacaoAdiantamentoMes(l);
  let adiantamentoCentavos = 0;
  if (l.adiantamento) adiantamentoCentavos = paraCentavos(l.adiantamento.valor);
  else if (situacao === "a_gerar") adiantamentoCentavos = paraCentavos(calcValorAdiantamento(l.salario_mensal, l.adiantamento_pct));
  return {
    salarioFixo: paraReais(paraCentavos(l.salario_mensal)),
    adiantamentosPagos: paraReais(adiantamentoCentavos),
    variavelApurada: 0,
    descontos: paraReais(l.descontos_centavos),
  };
}

/**
 * Líquido da linha: `liquido_final` GRAVADO quando há fechamento (invariante 3
 * — snapshot manda), senão a projeção via `calcLiquidoFechamento` — nunca uma
 * segunda conta "parecida".
 */
export function liquidoLinhaMes(
  l: Parameters<typeof argsProjecaoLinhaMes>[0] & Pick<LinhaMensalistaMes, "fechamento">,
): string {
  if (l.fechamento) return paraReais(paraCentavos(l.fechamento.liquido_final));
  return calcLiquidoFechamento(argsProjecaoLinhaMes(l));
}

/** Adiantamento em centavos como a coluna do mês o mostra (snapshot ou projeção). */
export function adiantamentoLinhaMesCentavos(
  l: Parameters<typeof argsProjecaoLinhaMes>[0] & Pick<LinhaMensalistaMes, "fechamento">,
): number {
  if (l.fechamento) return paraCentavos(l.fechamento.adiantamentos_pagos);
  return paraCentavos(argsProjecaoLinhaMes(l).adiantamentosPagos);
}

export interface ResumoDoMes {
  estado: EstadoDoMes;
  /** Σ `liquido_final` dos ABERTOS quando há fechamento; senão Σ projeção. */
  liquidoAPagarCentavos: number;
  /** Σ `liquido_final` dos FECHADOS — o "R$ X pago em dd/mm" da faixa. */
  liquidoPagoCentavos: number;
  salariosCentavos: number;
  /** TOTAL de adiantamentos que o líquido abate (pagos + previstos) — o VALOR do KPI. */
  adiantamentosCentavos: number;
  nAdiantamentos: number;
  /**
   * Separação por estado REAL (situacao 'pago_quinzena' | 'pago_fora', ou o
   * snapshot `fechamento.adiantamentos_pagos`) × 'previsto' | 'a_gerar'. O
   * valor do cartão é o total; o RÓTULO tem de dizer a verdade sobre ele — um
   * "já pago" em cima de dinheiro previsto é a mesma classe de erro que pagou
   * salário cheio ao Dáu, na direção contrária. Invariante:
   * `adiantamentosPagosCentavos + adiantamentosPrevistosCentavos === adiantamentosCentavos`.
   */
  adiantamentosPagosCentavos: number;
  adiantamentosPrevistosCentavos: number;
  nAdiantamentosPagos: number;
  /** Inclui os 'a_gerar'. */
  nAdiantamentosPrevistos: number;
  adicionaisCentavos: number;
  descontosCentavos: number;
  nAdicionais: number;
  nDescontos: number;
  nMensalistas: number;
  nAbertos: number;
  nFechados: number;
  nAGerar: number;
}

/** KPIs e faixa de estado da aba Mês, tudo de uma passada só sobre as linhas. */
export function resumirMes(linhas: LinhaMensalistaMes[]): ResumoDoMes {
  const estado = estadoDoMes(linhas);
  const r: ResumoDoMes = {
    estado,
    liquidoAPagarCentavos: 0, liquidoPagoCentavos: 0,
    salariosCentavos: 0, adiantamentosCentavos: 0, nAdiantamentos: 0,
    adiantamentosPagosCentavos: 0, adiantamentosPrevistosCentavos: 0,
    nAdiantamentosPagos: 0, nAdiantamentosPrevistos: 0,
    adicionaisCentavos: 0, descontosCentavos: 0, nAdicionais: 0, nDescontos: 0,
    nMensalistas: 0, nAbertos: 0, nFechados: 0, nAGerar: 0,
  };
  const existeFechamento = linhas.some(l => l.fechamento);
  for (const l of linhas) {
    // Linha só histórica (era mensalista, sem fechamento) não entra em dinheiro
    // nenhum do mês — não vai ter fechamento.
    if (l.era_mensalista && !l.fechamento) continue;
    r.nMensalistas += 1;
    const estadoLinha = estadoLinhaMes(l);
    const liquido = paraCentavos(liquidoLinhaMes(l));
    if (estadoLinha === "fechado") { r.nFechados += 1; r.liquidoPagoCentavos += liquido; }
    else if (estadoLinha === "aberto") { r.nAbertos += 1; r.liquidoAPagarCentavos += liquido; }
    else if (!existeFechamento) r.liquidoAPagarCentavos += liquido;
    r.salariosCentavos += paraCentavos(salarioLinhaMes(l));
    const adiantado = adiantamentoLinhaMesCentavos(l);
    const situacao = situacaoAdiantamentoMes(l);
    if (adiantado > 0) {
      r.adiantamentosCentavos += adiantado;
      r.nAdiantamentos += 1;
      // Com fechamento, `adiantado` É o snapshot `adiantamentos_pagos` — e o
      // snapshot só grava o que estava PAGO na geração. Sem fechamento, a
      // classificação é a mesma de `situacaoAdiantamentoMes` (um critério só).
      const pago = !!l.fechamento || situacao === "pago_quinzena" || situacao === "pago_fora";
      if (pago) { r.adiantamentosPagosCentavos += adiantado; r.nAdiantamentosPagos += 1; }
      else { r.adiantamentosPrevistosCentavos += adiantado; r.nAdiantamentosPrevistos += 1; }
    }
    r.adicionaisCentavos += l.adicionais_centavos;
    r.descontosCentavos += l.descontos_centavos;
    for (const lan of l.lancamentos) {
      if (lan.sinal === "DESCONTO") r.nDescontos += 1; else r.nAdicionais += 1;
    }
    if (situacao === "a_gerar") r.nAGerar += 1;
  }
  return r;
}

export interface RotuloKpiAdiantamentos {
  rotulo: string;
  legenda: string;
  /** A legenda é um alerta ("N a gerar") e vai em vermelho. */
  legendaVermelha: boolean;
  /** No caso misto, o "N a gerar" que acompanha a legenda (vermelho); null quando não há. */
  aGerar: string | null;
}

/**
 * O que o KPI de adiantamentos da aba Mês AFIRMA sobre o valor que mostra.
 * O valor é sempre `adiantamentosCentavos` (o total que o líquido abate — não
 * pode divergir da coluna); o rótulo é escolhido pelo que existe de verdade:
 *
 *   tudo pago  → "Já pago nas quinzenas" · N adiantamento(s)
 *   nada pago  → "Adiantamentos previstos" · "N a gerar" (vermelho) ou "N previsto(s)"
 *   misto      → "Adiantamentos" · "R$ X pagos · R$ Y previstos" (+ "N a gerar" vermelho)
 *
 * Total 0 mantém o rótulo antigo: não há dinheiro sobre o qual mentir.
 * É texto que descreve dinheiro — função pura, com teste, não ternária no JSX.
 */
export function rotuloKpiAdiantamentos(
  r: Pick<ResumoDoMes, "adiantamentosCentavos" | "adiantamentosPagosCentavos" | "adiantamentosPrevistosCentavos"
    | "nAdiantamentos" | "nAdiantamentosPagos" | "nAdiantamentosPrevistos" | "nAGerar">,
): RotuloKpiAdiantamentos {
  const plural = (n: number, s: string, p: string) => `${n} ${n === 1 ? s : p}`;
  const aGerar = r.nAGerar > 0 ? `${r.nAGerar} a gerar` : null;
  if (r.adiantamentosCentavos <= 0) {
    return aGerar
      ? { rotulo: "Já pago nas quinzenas", legenda: aGerar, legendaVermelha: true, aGerar: null }
      : { rotulo: "Já pago nas quinzenas", legenda: plural(r.nAdiantamentos, "adiantamento", "adiantamentos"), legendaVermelha: false, aGerar: null };
  }
  if (r.adiantamentosPrevistosCentavos === 0) {
    return { rotulo: "Já pago nas quinzenas", legenda: plural(r.nAdiantamentosPagos, "adiantamento", "adiantamentos"), legendaVermelha: false, aGerar: null };
  }
  if (r.adiantamentosPagosCentavos === 0) {
    return aGerar
      ? { rotulo: "Adiantamentos previstos", legenda: aGerar, legendaVermelha: true, aGerar: null }
      : { rotulo: "Adiantamentos previstos", legenda: plural(r.nAdiantamentosPrevistos, "previsto", "previstos"), legendaVermelha: false, aGerar: null };
  }
  return {
    rotulo: "Adiantamentos",
    legenda: `${formatarBRL(paraReais(r.adiantamentosPagosCentavos))} pagos · ${formatarBRL(paraReais(r.adiantamentosPrevistosCentavos))} previstos`,
    legendaVermelha: false,
    aGerar,
  };
}

/**
 * O bloco vermelho de pendências da aba Mês aparece? Só quando a competência
 * tem PELO MENOS UMA quinzena: mês sem quinzena não tem pendência — não há o
 * que gerar, e a faixa de estado já explica ("crie a quinzena no Financeiro").
 * Todo mês futuro nasceria gritando, e alarme que grita sempre é alarme que
 * ninguém lê (o aviso âmbar do FecharQuinzenaSheet foi ignorado assim em agosto).
 *
 * É VISIBILIDADE, não regra: a trava do servidor (409 ADIANTAMENTO_PENDENTE no
 * PATCH /fechamentos/:id) não depende disto.
 */
export function exibirPendenciasDoMes(args: { competenciaTemQuinzena: boolean; nPendencias: number }): boolean {
  return args.competenciaTemQuinzena && args.nPendencias > 0;
}

/**
 * Σ dos salários da competência que ainda vão sair no fechamento — o número da
 * faixa "Salários de agosto: R$ X em sáb 05/09" da aba Quinzena (e, até o
 * Ciclo 2, do cartão "Fecha mês em").
 *
 * Com fechamento gerado: Σ `liquido_final` dos ABERTOS (se todos já estão
 * FECHADOS o valor real É zero — nada mais a sair, não uma reestimativa).
 * Sem fechamento: projeção fixo − adiantamento (QUINZENAL) de cada mensalista
 * desta quinzena. Movida de financeiro.tsx para ser UMA função nos dois lugares.
 *
 * O adiantamento que abate aqui é o da COMPETÊNCIA (`adiantamento_comp_valor`),
 * não o desta quinzena: a pergunta é "quanto do salário do mês ainda vai sair",
 * e quem recebeu metade na primeira quinzena do mês já a recebeu — projetar
 * salário cheio na segunda anunciaria dinheiro que não vai sair. Cai em
 * `adiantamento_valor` quando a linha não traz a coluna nova (origem antiga).
 * Isto é PROJEÇÃO de exibição; nenhum caixa passa por aqui.
 */
export function totalSalariosDoMesCentavos(args: {
  fechamentos: Array<{ status: string; liquidoFinal: string | number | null | undefined }>;
  mensalistas: Array<{
    salario_mensal: string | number | null | undefined;
    forma_pagamento: string | null;
    adiantamento_valor: string | number | null | undefined;
    adiantamento_comp_valor?: string | number | null | undefined;
    era_mensalista?: boolean;
  }>;
}): { centavos: number; previsto: boolean } {
  if (args.fechamentos.length > 0) {
    return {
      centavos: args.fechamentos
        .filter(f => f.status === "ABERTO")
        .reduce((acc, f) => acc + paraCentavos(f.liquidoFinal), 0),
      previsto: false,
    };
  }
  return {
    centavos: args.mensalistas.reduce((acc, m) => {
      if (m.era_mensalista) return acc;
      const fixo = paraCentavos(m.salario_mensal);
      const adiantado = (m.forma_pagamento ?? "MENSAL") === "QUINZENAL"
        ? paraCentavos(m.adiantamento_comp_valor ?? m.adiantamento_valor)
        : 0;
      return acc + (fixo - adiantado);
    }, 0),
    previsto: true,
  };
}
